import { useState, type SyntheticEvent } from 'react';

import { Input } from '@/core/tamagoshi/Input/Input';
import { Spacing } from '@/core/tamagoshi/Spacing/Spacing';
import { Text } from '@/core/tamagoshi/Text/Text';
import { useMutation } from '@/domains/core/httpClient/queryClient';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import {
  createNewWishlistModalActionButtonText,
  createNewWishlistModalInputError,
  createNewWishlistModalLabel,
} from '@/domains/proB2BServices/Wishlists/modules/WishlistNavigation/Operations/translations';

import { useWishlistServices } from '../../Wishlists/hooks/useWishlistServices';
import type { WishlistUnit } from '../../Wishlists/interfaces';
import { AlertRedExclamation } from '../components/icons/AlertRedExclamation';
import { ID_WISHLIST_ALL_PRODUCTS } from '../constants';
import { GTM_EVENTS_DATA } from '../gtmEvents';
import {
  addToWishlistCreateSubTitle,
  addToWishlistCreateTitle,
  wishlistNameBeyondMaxLimit,
  wishlistRestrictedName,
} from '../translations';

import style from '../fragments.module.scss';

export const useNewList = (
  onAddToList: (id: string | number, listName: string) => void,
  onError: () => void,
) => {
  const [value, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { calls } = useWishlistServices();

  const cleanUp = () => {
    setErrorMessage('');
    setInputValue('');
  };

  const { mutate: onCreateList } = useMutation({
    mutationFn: async (nameList: string) => {
      const trimmedValue = String(nameList).trim();
      if (trimmedValue == ID_WISHLIST_ALL_PRODUCTS) {
        setErrorMessage(wishlistRestrictedName);
        return;
      }
      if (trimmedValue.length > 200) {
        setErrorMessage(wishlistNameBeyondMaxLimit);
        return;
      }
      return calls.create(nameList);
    },
  });

  const onCreate = () => {
    Gtm.push({
      ...GTM_EVENTS_DATA.CREATE_LIST,
      event_trigger: 'Créer',
    });
    if (value?.length > 0) {
      onCreateList(value, {
        onSuccess: (content: WishlistUnit | undefined) => {
          if (!content) return;
          const { id, name } = content;
          cleanUp();
          onAddToList(id, name);
        },
        onError,
      });
    } else {
      setErrorMessage(createNewWishlistModalInputError);
    }
  };

  const handleInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
    event.preventDefault();
    setInputValue(event.currentTarget.value.replace(/["$'()/?\\^{}]/g, ''));
  };

  const body = (
    <>
      <Spacing type="mb" size={16}>
        <Text tag="p" className={style.subtitle}>
          {addToWishlistCreateSubTitle}
        </Text>
      </Spacing>
      <Input
        data-testid="body-input"
        label={createNewWishlistModalLabel}
        value={value}
        message={
          errorMessage && (
            <span className={style.wishlistAlertMessage}>
              <AlertRedExclamation />
              <span>{errorMessage}</span>
            </span>
          )
        }
        onChange={handleInputChange}
      />
    </>
  );

  return {
    body,
    title: addToWishlistCreateTitle,
    actionButtonText: createNewWishlistModalActionButtonText,
    onActionSuccess: onCreate,
    onActionClose: cleanUp,
  };
};
